import React, { useEffect } from 'react'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Typography,
} from '@mui/material'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { styled } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import { StructuralSettings } from '../models/Structural'
import {
  fetchAllObjectSettings,
  updateObjectSettings,
} from '../services/structuralService'
import { showLoader, showStructuralSettingsModal } from '../actions/structural'
import { Close } from '@mui/icons-material'

interface BuildingObjectSettingsProps {
  open: boolean
}

export const UpdateStructuralSettings: React.FC<
  BuildingObjectSettingsProps
> = ({ open }) => {
  const dispatch = useDispatch<any>()

  return (
    <Modal
      open={open}
      onClose={() => dispatch(showStructuralSettingsModal(false))}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
      }}
    >
      <Box
        sx={{
          width: '40%',
          gap: 1,
          padding: '10px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '22px',
            }}
          >
            Object Settings
          </Typography>
          <IconButton
            onClick={() => dispatch(showStructuralSettingsModal(false))}
            sx={{ position: 'absolute', right: 10, top: 10 }}
          >
            <Close />
          </IconButton>
        </Box>
        <Divider flexItem sx={{ marginBottom: '10px' }} />
        <ObjectList />
      </Box>
    </Modal>
  )
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#E5F0FF',
  },
  '&& .Mui-selected': {
    backgroundColor: '#E5F0FF',
  },
}))

const ObjectList = () => {
  const dispatch = useDispatch<any>()
  const selectedFile = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )
  const defaultStructuralSettings = useSelector(
    (state: RootState) => state.structural.defaultStructuralSettings,
  )
  const isShowLoader = useSelector(
    (state: RootState) => state.structural.showLoader,
  )
  useEffect(() => {
    dispatch(showLoader(true))
    dispatch(fetchAllObjectSettings({ file_id: selectedFile?.id! }))
  }, [])

  const structuralSettings = useSelector(
    (state: RootState) => state.structural.objectSettings,
  )
  const [selectedSettingCategory, setSelectedSettingCategory] = React.useState<
    string | null
  >(defaultStructuralSettings?.[0].category)
  const [editedRows, setEditedRows] = React.useState<{
    [key: number]: StructuralSettings
  }>({})

  const settings =
    structuralSettings.length > 0
      ? defaultStructuralSettings.map((setting: StructuralSettings) => {
          const existingSetting = structuralSettings.find(
            (s) =>
              s.category === setting.category &&
              s.variable === setting.variable,
          )
          let newSetting = {
            ...setting,
            value:
              existingSetting && existingSetting.value
                ? existingSetting.value
                : setting.value,
          }
          if (existingSetting?.id) newSetting.id = existingSetting.id
          return newSetting
        })
      : defaultStructuralSettings

  const filteredData = React.useMemo(
    () =>
      settings.filter(
        (objectSettings: StructuralSettings) =>
          objectSettings.category === selectedSettingCategory,
      ),
    [selectedSettingCategory],
  )

  const handlevalueChange = (index: number, value: string) => {
    const parsedValue = value ? parseFloat(parseFloat(value).toFixed(2)) : 0
    const updated = { ...filteredData[index], value: parsedValue }
    setEditedRows((prev) => ({ ...prev, [index]: updated }))
  }

  const handleSave = () => {
    const defaultRows = filteredData.filter(
      (row: StructuralSettings) => !row.id,
    )
    const editedExistingRows = Object.values(editedRows).filter((row) => row.id)
    const rows = [...defaultRows, ...editedExistingRows]
    const rowsToUpdate = rows.map((row) => ({
      ...row,
      file_id: selectedFile?.id,
    }))

    dispatch(updateObjectSettings({ settings: rowsToUpdate }))
    dispatch(showLoader(true))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
      }}
    >
      {isShowLoader ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
          }}
        >
          <CircularProgress sx={{ width: '100%', height: '100%' }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              width: '35%',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontStyle: 'Bold',
                fontSize: '16px',
                lineHeight: '100%',
                padding: '10px',
                letterSpacing: '0%',
                verticalAlign: 'middle',
                color: '#04002E',
                background: '#F5F5F5',
              }}
            >
              Object
            </Typography>
            <List
              style={{
                maxHeight: '60vh',
                overflowY: 'auto',
                background: '#FBFBFB',
              }}
            >
              {Array.from(
                new Set(
                  settings?.map(
                    (objectSettings: StructuralSettings) =>
                      objectSettings.category,
                  ),
                ),
              ).map((category: string, index: number) => (
                <StyledListItem key={index} disablePadding>
                  <ListItemButton
                    selected={category === selectedSettingCategory}
                    style={{ margin: 0, padding: '5px 16px' }}
                    onClick={() => setSelectedSettingCategory(category)}
                  >
                    <ListItemText
                      primary={category}
                      style={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontStyle: 'Medium',
                        fontSize: '18px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        verticalAlign: 'middle',
                      }}
                    />
                  </ListItemButton>
                </StyledListItem>
              ))}
            </List>
          </Box>
          <Box
            sx={{
              width: '65%',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontStyle: 'Bold',
                fontSize: '16px',
                lineHeight: '100%',
                padding: '10px',
                letterSpacing: '0%',
                verticalAlign: 'middle',
                color: '#04002E',
                background: '#F5F5F5',
              }}
            >
              {selectedSettingCategory}
            </Typography>
            <Box
              sx={{
                padding: '10px',
                maxHeight: '40vh',
                overflowY: 'auto',
                background: '#FBFBFB',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#fff',
                }}
              >
                <tbody>
                  {filteredData.map(
                    (objectSettings: StructuralSettings, index: number) => {
                      // Use edited value if present, else original
                      const edited = Object.values(editedRows).find(
                        (row: StructuralSettings) =>
                          row.category === objectSettings.category &&
                          row.variable === objectSettings.variable,
                      )
                      const displayValue = edited
                        ? edited.value
                        : objectSettings.value
                      return (
                        <tr
                          key={index}
                          style={{
                            borderBottom: '1px solid #eee',
                            height: '40px',
                          }}
                        >
                          {/* Hidden id column for internal use */}
                          <td style={{ display: 'none' }}>
                            {objectSettings.id ?? ''}
                          </td>
                          <td
                            style={{
                              width: '50%',
                              fontSize: 14,
                              fontFamily: 'Inter',
                              fontWeight: 400,
                              fontStyle: 'Regular',
                              lineHeight: '100%',
                              letterSpacing: '0%',
                              verticalAlign: 'middle',
                              color: '#737373',
                            }}
                          >
                            {objectSettings.variable}
                          </td>
                          <td
                            style={{
                              width: '20%',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                background: '#F5F5F5',
                              }}
                            >
                              <input
                                type='text'
                                value={displayValue}
                                style={{
                                  width: '90%',
                                  fontSize: 14,
                                  background: '#F5F5F5',
                                  fontFamily: 'Inter',
                                  fontWeight: 400,
                                  fontStyle: 'Regular',
                                  lineHeight: '100%',
                                  letterSpacing: '0%',
                                  verticalAlign: 'middle',
                                  color: '#737373',
                                  marginBottom: '0px',
                                }}
                                onChange={(e) =>
                                  handlevalueChange(index, e.target.value)
                                }
                              />
                              <div
                                style={{
                                  alignContent: 'center',
                                  fontSize: 14,
                                  fontFamily: 'Inter',
                                  fontWeight: 400,
                                  fontStyle: 'Regular',
                                  lineHeight: '100%',
                                  letterSpacing: '0%',
                                  verticalAlign: 'middle',
                                  color: '#737373',
                                }}
                              >
                                {objectSettings.unit}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )
                    },
                  )}
                </tbody>
              </table>
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <button
                  style={{
                    padding: '6px 18px',
                    background: '#56003D',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSave()}
                  disabled={
                    Object.keys(editedRows).length === 0 &&
                    filteredData.filter((row: StructuralSettings) => !row.id)
                      .length === 0
                  }
                >
                  Save Changes
                </button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
